import {
  AD_MIN_WIDTH_PX,
  AD_MIN_HEIGHT_PX,
  AD_MOBILE_HEIGHT,
  AD_MOBILE_WIDTH,
  LIMIT_SIDE_PX,
  MAX_VALUE_WIDTH_HEIGHT_PX,
} from '@app/constants';
import { ADUNIT_SIZE, type AdunitSize, type Dimensions } from '@app/types';

export interface ConstrainedSizeResult {
  needsResize: boolean;
  target: Dimensions | null;
}

/**
 * Calculate the target size based on ad unit constraints.
 * Returns null if no resize is needed.
 *
 * Used by:
 * - useLimitResize hook (Classic Editor - via widget)
 * - editor.app.ts (Studio - via editor script)
 */
export function getConstrainedSize(
  currentWidth: number,
  currentHeight: number,
  sizeType: AdunitSize | undefined,
  fixedDimensions: Dimensions | undefined,
  isMobile: boolean = false,
): ConstrainedSizeResult {
  // Mobile: always 300x250
  if (isMobile) {
    const needsResize =
      currentWidth !== AD_MOBILE_WIDTH || currentHeight !== AD_MOBILE_HEIGHT;
    return {
      needsResize,
      target: needsResize
        ? { width: AD_MOBILE_WIDTH, height: AD_MOBILE_HEIGHT }
        : null,
    };
  }

  // Fixed ads: enforce exact dimensions
  if (sizeType === ADUNIT_SIZE.FIXED && fixedDimensions) {
    const needsResize =
      currentWidth !== fixedDimensions.width ||
      currentHeight !== fixedDimensions.height;
    return {
      needsResize,
      target: needsResize ? fixedDimensions : null,
    };
  }

  // Responsive ads: enforce min/max constraints
  if (sizeType === ADUNIT_SIZE.RESPONSIVE || !sizeType) {
    let width = currentWidth;
    let height = currentHeight;
    let needsResize = false;

    // Min constraints
    if (width < AD_MIN_WIDTH_PX) {
      width = AD_MIN_WIDTH_PX;
      needsResize = true;
    }
    if (height < AD_MIN_HEIGHT_PX) {
      height = AD_MIN_HEIGHT_PX;
      needsResize = true;
    }

    // Max constraints (only for responsive, not for "no ad connected")
    if (sizeType === ADUNIT_SIZE.RESPONSIVE) {
      const bothLarge = width > LIMIT_SIDE_PX && height > LIMIT_SIDE_PX;

      if (bothLarge) {
        width = LIMIT_SIDE_PX;
        height = LIMIT_SIDE_PX;
        needsResize = true;
      } else {
        if (width > MAX_VALUE_WIDTH_HEIGHT_PX) {
          width = MAX_VALUE_WIDTH_HEIGHT_PX;
          needsResize = true;
        }
        if (height > MAX_VALUE_WIDTH_HEIGHT_PX) {
          height = MAX_VALUE_WIDTH_HEIGHT_PX;
          needsResize = true;
        }
      }
    }

    return {
      needsResize,
      target: needsResize ? { width, height } : null,
    };
  }

  return { needsResize: false, target: null };
}
